export type TinyLlamaInitOptions = {
  modelId?: string;
  onProgress?: (loaded: number, total: number, stage?: string) => void;
  model_lib?: string;
  model?: string;
  appConfig?: any;
};

export type GenerateOptions = {
  prompt: string;
  maxTokens?: number;
  temperature?: number;
  topP?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  stopSequences?: string[];
  onToken?: (token: string) => void;
  onDone?: (full: string) => void;
};

const isLocalDevRuntime = typeof window !== 'undefined' && /^http:\/\/localhost:8080$/i.test(window.location.origin);
const MODEL_BASE_URL = isLocalDevRuntime ? "/mlc-models" : "https://models.mapleos.ai";
const WEB_LLM_BASE_URL = isLocalDevRuntime ? "/web-llm-models/v0_2_80" : "https://mapleos.ai/web-llm-models/v0_2_80";

export class TinyLlamaWebGpuRuntime {
  private engine: any | null = null;
  private webllm: any | null = null;
  private modelId: string;

  constructor(modelId = "Llama-3.2-1B-Instruct-q4f32_1-MLC") {
    this.modelId = modelId;
  }

  static isWebGpuSupported(): boolean {
    return typeof navigator !== "undefined" && !!(navigator as any).gpu;
  }

  static async getWebGpuInfo(): Promise<{ adapterName?: string; limits?: any } | null> {
    if (!TinyLlamaWebGpuRuntime.isWebGpuSupported()) return null;
    try {
      const isWindows = typeof navigator !== 'undefined' && /win/i.test(navigator.platform);
      const adapter = await (navigator as any).gpu.requestAdapter(
        isWindows ? undefined : { powerPreference: "high-performance" }
      );
      if (!adapter) return null;
      return {
        adapterName: (adapter as any).name || "Detected",
        limits: (adapter as any).limits,
      };
    } catch {
      return null;
    }
  }

  async init(options?: TinyLlamaInitOptions): Promise<void> {
    if (!TinyLlamaWebGpuRuntime.isWebGpuSupported()) {
      throw new Error("WebGPU is not supported in this browser.");
    }

    const modelId = this.modelId;
    const baseAppConfig = options?.appConfig ?? {};

    const appConfig = {
      ...baseAppConfig,
      model_list: [
        {
          model: `${MODEL_BASE_URL}/Llama-3.2-1B-Instruct-q4f32_1-MLC/`,
          model_id: "Llama-3.2-1B-Instruct-q4f32_1-MLC",
          model_lib: `${WEB_LLM_BASE_URL}/Llama-3.2-1B-Instruct-q4f32_1-ctx4k_cs1k-webgpu.wasm`,
          vram_required_MB: 1128.82,
          low_resource_required: true,
          overrides: {
            context_window_size: 4096,
          },
        },
      ],
      useIndexedDBCache: true,
      useCacheApi: false,
      modelConfigOverride: {
        ...(baseAppConfig.modelConfigOverride ?? {}),
        sliding_window_size: -1,
      },
    };

    (globalThis as any).mlc_app_config = appConfig;

    if (!this.webllm) {
      this.webllm = await import("@mlc-ai/web-llm/runtime");
    }

    const engineConfig: any = {
      initProgressCallback: (p: any) => {
        if (options?.onProgress) {
          const loaded = Math.floor((p.progress || 0) * 100);
          options.onProgress(loaded, 100, p.text);
        }
      },
      use_web_worker: false,
      appConfig,
    };

    this.engine = await this.webllm.CreateMLCEngine(modelId, engineConfig);
  }

  async generate(opts: GenerateOptions): Promise<string> {
    if (!this.engine) {
      throw new Error("Engine not initialized");
    }

    const {
      prompt,
      maxTokens = 768,
      temperature = 0.7,
      topP = 0.9,
      frequencyPenalty = 0.2,
      presencePenalty = 0.0,
      stopSequences = [],
      onToken,
      onDone,
    } = opts;

    const engine = this.engine;
    let full = "";

    const systemPrompt =
      "You are a helpful on-device assistant. Be concise and practical. Avoid unnecessary verbosity.";

    const completionParams: any = {
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: String(prompt || "") },
      ],
      stream: true,
      temperature,
      top_p: topP,
      max_tokens: maxTokens,
      frequency_penalty: frequencyPenalty,
      presence_penalty: presencePenalty,
    };

    if (stopSequences && stopSequences.length > 0) {
      completionParams.stop = stopSequences;
    }

    try {
      const asyncChunkGenerator = await engine.chat.completions.create(completionParams);
      for await (const chunk of asyncChunkGenerator) {
        const choice = chunk?.choices?.[0];
        const delta = choice?.delta?.content ?? "";
        if (delta) {
          full += delta;
          onToken?.(delta);
        }
      }

      onDone?.(full);
      return full;
    } catch (e: any) {
      const msg = String(e?.message || e);
      throw new Error(msg || "Local WebGPU generation failed.");
    }
  }

  async dispose(): Promise<void> {
    this.engine = null;
  }
}

export default TinyLlamaWebGpuRuntime;